import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { staffingApi } from "@/api";
import {
  Calculator, RefreshCw, AlertTriangle, CheckCircle, Zap,
  DollarSign, IndianRupee, Sparkles, ChevronDown, Play,
  Save, Trash2, TrendingUp, Users, Plane, Activity,
  Award, BarChart3, ChevronRight, Info, X
} from "lucide-react";

// ─── Aircraft Types ────────────────────────────────────────────────────────────
const AIRCRAFT_TYPES = [
  { id: "atr72",     label: "ATR 72-600",              category: "regional",   seats: 70  },
  { id: "e190",      label: "Embraer E190",            category: "regional",   seats: 97  },
  { id: "a220-300",  label: "Airbus A220-300",         category: "narrowbody", seats: 130 },
  { id: "b737-700",  label: "Boeing 737-700",          category: "narrowbody", seats: 128 },
  { id: "a319neo",   label: "Airbus A319neo",          category: "narrowbody", seats: 120 },
  { id: "a320",      label: "Airbus A320",             category: "narrowbody", seats: 150 },
  { id: "b737-800",  label: "Boeing 737-800",          category: "narrowbody", seats: 162 },
  { id: "b737max8",  label: "Boeing 737 MAX 8",        category: "narrowbody", seats: 189 },
  { id: "a320neo",   label: "Airbus A320neo",          category: "narrowbody", seats: 165 },
  { id: "b737-900",  label: "Boeing 737-900ER",        category: "narrowbody", seats: 178 },
  { id: "a321",      label: "Airbus A321",             category: "narrowbody", seats: 185 },
  { id: "a321neo",   label: "Airbus A321neo",          category: "narrowbody", seats: 194 },
  { id: "b737max9",  label: "Boeing 737 MAX 9",        category: "narrowbody", seats: 193 },
  { id: "b737max10", label: "Boeing 737 MAX 10",       category: "narrowbody", seats: 204 },
  { id: "a321xlr",   label: "Airbus A321XLR",          category: "narrowbody", seats: 200 },
  { id: "b767-300",  label: "Boeing 767-300ER",        category: "widebody",   seats: 218 },
  { id: "a330-200",  label: "Airbus A330-200",         category: "widebody",   seats: 247 },
  { id: "b787-8",    label: "Boeing 787-8",            category: "widebody",   seats: 242 },
  { id: "a330-300",  label: "Airbus A330-300",         category: "widebody",   seats: 277 },
  { id: "b787-9",    label: "Boeing 787-9",            category: "widebody",   seats: 296 },
  { id: "b777-300",  label: "Boeing 777-300ER",        category: "widebody",   seats: 396 },
  { id: "a350-900",  label: "Airbus A350-900",         category: "widebody",   seats: 315 },
  { id: "a380",      label: "Airbus A380-800",         category: "vla",        seats: 555 },
];

const AC_CATEGORIES = [
  { id: "regional",   label: "Regional (30–100 seats)"     },
  { id: "narrowbody", label: "Narrowbody (100–220 seats)"  },
  { id: "widebody",   label: "Widebody (220–450 seats)"    },
  { id: "vla",        label: "Very Large Aircraft (450+)"  },
];

const ROUTE_META = {
  short_haul:      { label: "Short Haul",      color: "text-blue-400",  bg: "bg-blue-400/10 border-blue-400/30" },
  medium_haul:     { label: "Medium Haul",     color: "text-green-400", bg: "bg-green-400/10 border-green-400/30" },
  long_haul:       { label: "Long Haul",       color: "text-amber-400", bg: "bg-amber-400/10 border-amber-400/30" },
  ultra_long_haul: { label: "Ultra Long Haul", color: "text-red-400",   bg: "bg-red-400/10 border-red-400/30" },
};

const PROFILES = {
  custom: { label: "Custom Airline", iata: "XX", aircraftTypeId: "a320neo", aircraft: 20, seats: 165, fhPerDay: 10, sectorsPerDay: 4, avgSectorLength: 2.0, maxPilotFH: 75, crewPerFlight: 4, dutiesPerCrew: 18, leavePct: 12, trainingPct: 8, sickPct: 5, reservePct: 15, maxSectorsPerCrewMonth: 100, maxDutiesPerCrewMonth: 20, sectorsPerDuty: 2, minPilotsPerAircraft: 3.5, actualPilots: 0, actualCabinCrew: 0, avgPilotCostLakhs: 3.5, avgCabinCrewCostLakhs: 1.0 },
};

// ─── Info Tooltip ─────────────────────────────────────────────────────────────
function InfoTooltip({ text, position = "top", width = "w-64" }) {
  const [show, setShow] = useState(false);
  const posMap = {
    top:    "bottom-full mb-2 left-1/2 -translate-x-1/2",
    bottom: "top-full mt-2 left-1/2 -translate-x-1/2",
    left:   "right-full mr-2 top-1/2 -translate-y-1/2",
    right:  "left-full ml-2 top-1/2 -translate-y-1/2",
    "top-right": "bottom-full mb-2 right-0",
    "top-left":  "bottom-full mb-2 left-0",
  };
  return (
    <span className="relative inline-flex items-center"
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <Info size={11} className="text-muted-foreground/40 hover:text-primary/70 cursor-help transition-colors shrink-0" />
      {show && (
        <span className={`absolute z-[9999] ${width} p-2.5 bg-popover border border-border rounded-sm shadow-2xl text-xs text-muted-foreground leading-relaxed whitespace-normal ${posMap[position] || posMap.top}`}>
          {text}
        </span>
      )}
    </span>
  );
}

// ─── Small Components ──────────────────────────────────────────────────────────
function NumInput({ label, value, onChange, testId, aiDerived, sub, min, max, step = 1 }) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground flex items-center gap-1">
        {label}
        {aiDerived && <Sparkles size={9} className="text-primary/60" />}
      </label>
      {sub && <p className="text-xs text-muted-foreground/40 -mt-0.5">{sub}</p>}
      <input
        type="number" value={value} min={min} max={max} step={step}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        data-testid={testId}
        className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-xs text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </div>
  );
}

function Slider({ label, value, min, max, step, unit, onChange, testId, aiDerived }) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center">
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          {label}{aiDerived && <Sparkles size={9} className="text-primary/60" />}
        </span>
        <span className="text-xs font-mono font-bold text-foreground">{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        data-testid={testId}
        className="w-full h-1.5 bg-secondary rounded-full appearance-none cursor-pointer accent-primary" />
      <div className="flex justify-between text-xs text-muted-foreground/40 font-mono">
        <span>{min}{unit}</span><span>{max}{unit}</span>
      </div>
    </div>
  );
}

function MethodCard({ method, value, isBinding, details, accentColor = "text-primary", icon: Icon = BarChart3, info }) {
  return (
    <div className={`relative rounded-sm border p-3 transition-all ${isBinding
      ? "border-primary bg-primary/5 ring-1 ring-primary/30"
      : "border-border bg-card/60"}`}>
      {isBinding && (
        <div className="absolute -top-2.5 left-3 flex items-center gap-1 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-sm font-bold">
          <Award size={9} /> BINDING
        </div>
      )}
      <div className="flex justify-between items-start mb-2 mt-1">
        <div className="flex items-center gap-1.5">
          <Icon size={13} className={isBinding ? "text-primary" : "text-muted-foreground/60"} />
          <span className="text-xs font-bold text-foreground">{method}</span>
          {info && <InfoTooltip text={info} position="top-left" width="w-72" />}
        </div>
        <span className={`text-lg font-mono font-black ${isBinding ? "text-primary" : accentColor}`}>{value}</span>
      </div>
      {details.map((d, i) => (
        <div key={i} className="flex justify-between items-center text-xs border-t border-border/30 pt-1 mt-1">
          <span className="text-muted-foreground">{d.label}</span>
          <span className="font-mono text-foreground/80">{d.val}</span>
        </div>
      ))}
    </div>
  );
}

function InsightBadge({ type, text }) {
  const cfg = {
    critical: { icon: <AlertTriangle size={11} className="text-red-400 shrink-0 mt-0.5" />, border: "border-red-400/20 bg-red-400/5" },
    warning:  { icon: <AlertTriangle size={11} className="text-amber-400 shrink-0 mt-0.5" />, border: "border-amber-400/20 bg-amber-400/5" },
    success:  { icon: <CheckCircle size={11} className="text-green-400 shrink-0 mt-0.5" />, border: "border-green-400/20 bg-green-400/5" },
    info:     { icon: <Info size={11} className="text-blue-400 shrink-0 mt-0.5" />, border: "border-blue-400/20 bg-blue-400/5" },
  };
  const c = cfg[type] || cfg.info;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-sm border ${c.border}`}>
      {c.icon}
      <span className="text-xs text-foreground/80 leading-relaxed">{text}</span>
    </div>
  );
}

function KpiCard({ label, value, sub, color = "text-primary", testId, info }) {
  return (
    <div className="bg-card border border-border rounded-sm p-3 text-center relative" data-testid={testId}>
      {info && (
        <div className="absolute top-2 right-2">
          <InfoTooltip text={info} position="top-right" width="w-72" />
        </div>
      )}
      <div className={`text-2xl font-mono font-black ${color}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1 leading-tight">{label}</div>
      {sub && (
        <div className={`text-xs font-mono mt-0.5 ${
          sub.startsWith("Enter") ? "text-muted-foreground/40 italic" : `${color}/70`
        }`}>{sub}</div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function StaffingEngine() {
  const [p, setP]                         = useState(PROFILES.custom);
  const [aircraftType, setAircraftType]   = useState(PROFILES.custom.aircraftTypeId);
  const [currency, setCurrency]           = useState("INR");
  const [exchangeRate, setExchangeRate]   = useState(null);
  const [rateLoading, setRateLoading]     = useState(false);
  const [rateNote, setRateNote]           = useState("");

  // AI
  const [aiLoading, setAiLoading]           = useState(false);
  const [aiRationale, setAiRationale]       = useState(null);
  const [aiDerivedFields, setAiDerivedFields] = useState(new Set());

  // Calculation
  const [calcLoading, setCalcLoading] = useState(false);
  const [results, setResults]         = useState(null);
  const [calcError, setCalcError]     = useState(null);

  // Save runs
  const [savedRuns, setSavedRuns]     = useState([]);
  const [saveLabel, setSaveLabel]     = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [showSaved, setShowSaved]     = useState(false);
  const [compareRuns, setCompareRuns] = useState([]);

  // FH manual override
  const [fhManualOverride, setFhManualOverride] = useState(false);

  const aiDebounceRef = useRef(null);
  const mountedRef    = useRef(false);
  const set = (field) => (val) => setP(prev => ({ ...prev, [field]: val }));

  const selectedAircraft = AIRCRAFT_TYPES.find(a => a.id === aircraftType) || AIRCRAFT_TYPES[7];

  // ── FH auto-derive ───────────────────────────────────────────────────────
  useEffect(() => {
    if (fhManualOverride) return;
    const derived = Math.round(p.sectorsPerDay * p.avgSectorLength * 10) / 10;
    if (derived !== p.fhPerDay) setP(prev => ({ ...prev, fhPerDay: derived }));
  }, [p.sectorsPerDay, p.avgSectorLength, fhManualOverride]); // eslint-disable-line

  // ── AI Suggest ───────────────────────────────────────────────────────────
  const callAiSuggest = useCallback(async (acType, params, clearResults = false) => {
    const ac = AIRCRAFT_TYPES.find(a => a.id === acType) || selectedAircraft;
    setAiLoading(true);
    try {
      const res = await staffingApi.aiSuggest({
        aircraft_id: ac.id, aircraft_label: ac.label,
        seats: ac.seats, category: ac.category,
        sectors_per_day: params.sectorsPerDay,
        avg_sector_length: params.avgSectorLength,
        fleet_size: params.aircraft,
      });
      const d = res.data;
      setP(prev => ({
        ...prev,
        maxPilotFH: d.maxPilotFH, crewPerFlight: d.crewPerFlight,
        dutiesPerCrew: d.dutiesPerCrew, leavePct: d.leavePct,
        trainingPct: d.trainingPct, sickPct: d.sickPct,
        reservePct: d.reservePct,
        ...(d.maxSectorsPerCrewMonth && { maxSectorsPerCrewMonth: d.maxSectorsPerCrewMonth }),
        ...(d.maxDutiesPerCrewMonth  && { maxDutiesPerCrewMonth:  d.maxDutiesPerCrewMonth }),
        ...(d.sectorsPerDuty         && { sectorsPerDuty:         d.sectorsPerDuty }),
        ...(d.minPilotsPerAircraft   && { minPilotsPerAircraft:   d.minPilotsPerAircraft }),
      }));
      setAiDerivedFields(new Set(["maxPilotFH", "crewPerFlight", "dutiesPerCrew", "leavePct",
        "trainingPct", "sickPct", "reservePct",
        "maxSectorsPerCrewMonth", "maxDutiesPerCrewMonth", "sectorsPerDuty", "minPilotsPerAircraft"]));
      setAiRationale({ route_classification: d.route_classification, rationale: d.rationale, cabin_crew_note: d.cabin_crew_note });
      if (clearResults) setResults(null); // only clear on explicit profile change
    } catch (e) { console.warn("AI suggest failed", e); }
    finally { setAiLoading(false); }
  }, []); // eslint-disable-line

  const scheduleAiSuggest = useCallback((acType, params, delay = 1200) => {
    clearTimeout(aiDebounceRef.current);
    setAiLoading(true);
    aiDebounceRef.current = setTimeout(() => callAiSuggest(acType, params), delay);
  }, [callAiSuggest]);

  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    scheduleAiSuggest(aircraftType, p, 1000);
  }, [aircraftType, p.sectorsPerDay, p.avgSectorLength]); // eslint-disable-line

  useEffect(() => {
    callAiSuggest(aircraftType, p);
    fetchRate();
    loadRuns();
  }, []); // eslint-disable-line

  const applyProfile = (key) => {
    const prof = PROFILES[key];
    setP(prof);
    setAircraftType(prof.aircraftTypeId);
    setFhManualOverride(false);
    setSaveLabel(prof.label);
    callAiSuggest(prof.aircraftTypeId, prof, true);
  };

  const fetchRate = async () => {
    setRateLoading(true);
    try {
      const r = await staffingApi.getExchangeRate();
      setExchangeRate(r.data.usd_to_inr);
      setRateNote(r.data.note || "");
    } catch { setExchangeRate(84.5); setRateNote("Fallback rate used"); }
    finally { setRateLoading(false); }
  };

  const loadRuns = async () => {
    try {
      const r = await staffingApi.getRuns();
      setSavedRuns(r.data || []);
    } catch (e) { console.warn("Could not load runs", e); }
  };

  // ── Run Calculation ─────────────────────────────────────────────────────
  const runCalculation = async () => {
    setCalcLoading(true);
    setCalcError(null);
    try {
      const payload = {
        airline_name:              p.label || PROFILES[activeProfile]?.label || "Custom Airline",
        planning_label:            "Monthly (30 days)",
        days_in_period:            30,
        fleet_size:                p.aircraft,
        seats:                     p.seats || selectedAircraft.seats,
        aircraft_type:             aircraftType,
        fh_per_day:                p.fhPerDay,
        sectors_per_day:           p.sectorsPerDay,
        max_pilot_fh_month:        p.maxPilotFH,
        max_sectors_per_crew_month: p.maxSectorsPerCrewMonth || 100,
        max_duties_per_crew_month: p.maxDutiesPerCrewMonth || 20,
        sectors_per_duty:          p.sectorsPerDuty || 2,
        min_pilots_per_aircraft:   p.minPilotsPerAircraft || 3.5,
        cc_per_flight:             p.crewPerFlight,
        max_cc_duties_per_month:   p.dutiesPerCrew,
        regulatory_cc_ratio:       50,
        industry_cc_ratio:         38,
        leave_pct:                 p.leavePct,
        training_pct:              p.trainingPct,
        sick_pct:                  p.sickPct,
        reserve_pct:               p.reservePct,
        avg_pilot_cost_lakhs:      p.avgPilotCostLakhs,
        avg_cc_cost_lakhs:         p.avgCabinCrewCostLakhs,
        actual_pilots:             p.actualPilots,
        actual_cabin_crew:         p.actualCabinCrew,
      };
      const res = await staffingApi.calculate(payload);
      setResults(res.data);
      setSaveLabel(payload.airline_name);
    } catch (e) {
      console.error("Calculation error", e);
      setCalcError("Calculation failed. Please check inputs and try again.");
    } finally { setCalcLoading(false); }
  };

  const handleSaveRun = async () => {
    if (!results || !saveLabel.trim()) return;
    setSaveLoading(true);
    try {
      const payload = { label: saveLabel.trim(), inputs: p, results };
      const res = await staffingApi.saveRun(payload);
      setSavedRuns(prev => [res.data, ...prev]);
      setShowSaved(true);
    } catch (e) { console.error("Save failed", e); }
    finally { setSaveLoading(false); }
  };

  const handleDeleteRun = async (id) => {
    try {
      await staffingApi.deleteRun(id);
      setSavedRuns(prev => prev.filter(r => r.id !== id));
      setCompareRuns(prev => prev.filter(r => r.id !== id));
    } catch (e) { console.error("Delete failed", e); }
  };

  const toggleCompare = (run) => {
    setCompareRuns(prev =>
      prev.find(r => r.id === run.id)
        ? prev.filter(r => r.id !== run.id)
        : prev.length < 3 ? [...prev, run] : prev
    );
  };

  // ── Format helpers ──────────────────────────────────────────────────────
  const fmtCr = (crores) => {
    if (crores === null || crores === undefined) return "—";
    if (currency === "USD" && exchangeRate) {
      const usd = (crores * 10_000_000) / exchangeRate;
      return `$${(usd / 1_000_000).toFixed(2)}M`;
    }
    return `₹${crores.toFixed(2)} Cr`;
  };
  const gapCol   = (g) => g > 0 ? "text-amber-400" : g < 0 ? "text-red-400" : "text-green-400";
  const gapLbl   = (g, actual) => actual === 0 ? "N/A" : g > 0 ? `+${g} Surplus` : g < 0 ? `${g} Shortage` : "Balanced";
  const gapBadge = (g, actual) => actual === 0 ? "text-muted-foreground border-border" :
    g > 0 ? "text-amber-400 border-amber-400/30 bg-amber-400/5" :
    g < 0 ? "text-red-400 border-red-400/30 bg-red-400/5" :
    "text-green-400 border-green-400/30 bg-green-400/5";

  const totalBuf   = p.leavePct + p.trainingPct + p.sickPct + p.reservePct;
  const routeMeta  = aiRationale ? (ROUTE_META[aiRationale.route_classification] || ROUTE_META.medium_haul) : null;
  const isDerived  = (f) => aiDerivedFields.has(f);

  const r = results;

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="staffing-engine">

      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">
            Fatigue Shield · Analytics Module
          </div>
          <h1 className="text-2xl font-black font-heading text-foreground flex items-center gap-2">
            <Calculator size={20} className="text-primary" />
            Crew Staffing Formula Engine
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Multi-method analysis: <span className="text-primary">MAX(Flight-Hour, Sector-Based, Duty-Day, Regulatory)</span>
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Currency toggle */}
          <div className="flex items-center gap-1 bg-card border border-border rounded-sm p-1">
            <button onClick={() => setCurrency("INR")}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-sm transition-colors ${currency === "INR" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="currency-inr"><IndianRupee size={11} /> INR</button>
            <button onClick={() => setCurrency("USD")}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-sm transition-colors ${currency === "USD" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="currency-usd"><DollarSign size={11} /> USD</button>
          </div>

          {/* Exchange rate */}
          <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-sm text-xs" data-testid="exchange-rate-badge">
            {rateLoading ? <span className="text-muted-foreground">Fetching rate...</span>
              : exchangeRate ? <span className="text-muted-foreground font-mono">₹{exchangeRate}/$1 <span className="text-primary/60">AI est.</span></span> : null}
            <button onClick={fetchRate} disabled={rateLoading} className="text-primary hover:text-primary/80 disabled:opacity-50" data-testid="refresh-rate-btn">
              <RefreshCw size={11} className={rateLoading ? "animate-spin" : ""} />
            </button>
          </div>

          {/* Saved runs toggle */}
          <button onClick={() => setShowSaved(!showSaved)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-sm border text-xs font-bold transition-colors ${showSaved ? "bg-primary/10 border-primary/30 text-primary" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}
            data-testid="toggle-saved-runs">
            <Save size={11} /> Saved Runs {savedRuns.length > 0 && `(${savedRuns.length})`}
          </button>
        </div>
      </div>

      {rateNote && currency === "USD" && (
        <div className="text-xs text-muted-foreground/60 bg-primary/5 border border-primary/10 rounded-sm px-3 py-2">
          AI exchange rate note: {rateNote}
        </div>
      )}

      {/* ─── Saved Runs Panel ─────────────────────────────────────────────── */}
      {showSaved && (
        <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="saved-runs-panel">
          <div className="flex items-center justify-between">
            <div className="text-xs tracking-widest uppercase font-bold text-muted-foreground">Saved Runs</div>
            {compareRuns.length > 0 && (
              <span className="text-xs text-primary font-mono">{compareRuns.length} selected for comparison</span>
            )}
          </div>
          {savedRuns.length === 0 ? (
            <p className="text-xs text-muted-foreground/50 italic">No saved runs yet. Run a calculation and save it.</p>
          ) : (
            <div className="space-y-2">
              {savedRuns.map(run => {
                const inCompare = compareRuns.find(r => r.id === run.id);
                return (
                  <div key={run.id} className={`flex items-center justify-between gap-3 p-2.5 rounded-sm border transition-colors ${inCompare ? "border-primary/40 bg-primary/5" : "border-border/60 bg-background/50"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-foreground truncate">{run.label}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {run.results?.required_pilots} pilots · {run.results?.required_cc} CC
                        <span className="ml-2 text-muted-foreground/50">{new Date(run.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleCompare(run)}
                        className={`text-xs px-2 py-1 rounded-sm border transition-colors font-bold ${inCompare ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                        {inCompare ? "Selected" : "Compare"}
                      </button>
                      <button onClick={() => handleDeleteRun(run.id)} className="text-red-400/60 hover:text-red-400 transition-colors" data-testid={`delete-run-${run.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Comparison table */}
          {compareRuns.length >= 2 && (
            <div className="mt-3 pt-3 border-t border-border overflow-x-auto">
              <div className="text-xs font-bold text-muted-foreground mb-2">Scenario Comparison</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="text-left py-1.5 font-bold pr-4">Metric</th>
                    {compareRuns.map(r => <th key={r.id} className="text-right py-1.5 font-bold px-3">{r.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Pilots Required",    get: r => r.results?.required_pilots },
                    { label: "Binding Method",      get: r => r.results?.binding_pilot_method },
                    { label: "Cabin Crew Req.",     get: r => r.results?.required_cc },
                    { label: "CC Binding Method",   get: r => r.results?.binding_cc_method?.split(" (")[0] },
                    { label: "Monthly Cost Impact", get: r => r.results?.cost ? `₹${(r.results.cost.total_monthly_crores).toFixed(2)} Cr` : "—" },
                    { label: "Annual Impact",       get: r => r.results?.cost ? `₹${(r.results.cost.total_annual_crores).toFixed(1)} Cr` : "—" },
                  ].map(row => (
                    <tr key={row.label} className="border-b border-border/30">
                      <td className="py-1.5 text-muted-foreground pr-4">{row.label}</td>
                      {compareRuns.map(run => (
                        <td key={run.id} className="py-1.5 text-right font-mono px-3 text-foreground">{row.get(run) ?? "—"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── Main Layout ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-5 gap-4 items-start">

        {/* ── Left: Inputs ──────────────────────────────────────────────── */}
        <div className="col-span-2 space-y-3">

          {/* Aircraft Type */}
          <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="aircraft-type-section">
            <div className="flex items-center justify-between">
              <div className="text-xs tracking-widest uppercase text-muted-foreground/60 font-bold">Aircraft Type</div>
              {aiLoading && (
                <div className="flex items-center gap-1.5 text-xs text-primary animate-pulse">
                  <Sparkles size={11} className="animate-spin" style={{ animationDuration: "2s" }} /> AI calibrating...
                </div>
              )}
            </div>
            <div className="relative">
              <select value={aircraftType} onChange={e => setAircraftType(e.target.value)}
                data-testid="aircraft-type-select"
                className="w-full bg-background border border-border rounded-sm px-2 py-2 pr-8 text-xs text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer">
                {AC_CATEGORIES.map(cat => (
                  <optgroup key={cat.id} label={cat.label}>
                    {AIRCRAFT_TYPES.filter(a => a.category === cat.id).map(ac => (
                      <option key={ac.id} value={ac.id}>{ac.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-2 bg-secondary/30 rounded-sm border border-border/40">
                <div className="text-base font-mono font-bold text-primary">{selectedAircraft.seats}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Seats</div>
              </div>
              <div className="text-center p-2 bg-secondary/30 rounded-sm border border-border/40">
                <div className="text-xs font-mono font-bold text-muted-foreground capitalize">{selectedAircraft.category}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Category</div>
              </div>
              <div className="text-center p-2 bg-secondary/30 rounded-sm border border-border/40">
                <div className="text-xs font-mono font-bold text-purple-400">{Math.ceil(selectedAircraft.seats / 38)}</div>
                <div className="text-xs text-muted-foreground mt-0.5">1:38 CC</div>
              </div>
            </div>
            {routeMeta && !aiLoading && (
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs font-bold ${routeMeta.bg} ${routeMeta.color}`}>
                <Sparkles size={10} /> {routeMeta.label}
              </div>
            )}
          </div>

          {/* Fleet & Operations */}
          <div className="bg-card border border-border rounded-sm p-4 space-y-3">
            <div className="text-xs tracking-widest uppercase text-muted-foreground/60 font-bold">Fleet &amp; Operations</div>
            <div className="grid grid-cols-2 gap-3">
              <NumInput label="Aircraft Count" value={p.aircraft} onChange={set("aircraft")} testId="input-aircraft" min={1} />
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-muted-foreground">Block FH / Day</label>
                  {fhManualOverride && (
                    <button onClick={() => setFhManualOverride(false)} className="text-xs text-primary/60 hover:text-primary" data-testid="reset-fh-btn">
                      Reset
                    </button>
                  )}
                </div>
                <input type="number" value={p.fhPerDay} step={0.1}
                  onChange={e => { setFhManualOverride(true); setP(prev => ({ ...prev, fhPerDay: parseFloat(e.target.value) || 0 })); }}
                  data-testid="input-fh-day"
                  className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-xs text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>
            <Slider label="Sectors / Day" value={p.sectorsPerDay} min={2} max={10} step={0.5} unit="" onChange={set("sectorsPerDay")} testId="slider-sectors" />
            <Slider label="Avg Sector Length (hrs)" value={p.avgSectorLength} min={0.5} max={14} step={0.1} unit="h" onChange={set("avgSectorLength")} testId="slider-sector-len" />
            <div className="text-xs text-muted-foreground/50 bg-secondary/20 rounded-sm px-2 py-1.5 font-mono">
              <div>{p.sectorsPerDay} sec × {p.avgSectorLength}h = {(p.sectorsPerDay * p.avgSectorLength).toFixed(1)}h auto · Block FH: <span className={`font-bold ${fhManualOverride ? "text-amber-400" : "text-primary"}`}>{p.fhPerDay}h{fhManualOverride ? " · manual" : ""}</span></div>
            </div>
          </div>

          {/* DGCA Regulatory Limits */}
          <div className="bg-card border border-border rounded-sm p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground/60 font-bold">
              DGCA Regulatory Limits
              {aiDerivedFields.size > 0 && !aiLoading && (
                <span className="flex items-center gap-1 text-primary/60 normal-case font-normal text-xs"><Sparkles size={9} /> AI-calibrated</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumInput label="Max Pilot FH / Month" value={p.maxPilotFH} aiDerived={isDerived("maxPilotFH")}
                onChange={v => { setAiDerivedFields(prev => { const n = new Set(prev); n.delete("maxPilotFH"); return n; }); set("maxPilotFH")(v); }}
                testId="input-max-fh" min={40} max={100} />
              <NumInput label="Max Sectors / Crew / Mo" value={p.maxSectorsPerCrewMonth || 100} aiDerived={isDerived("maxSectorsPerCrewMonth")}
                onChange={v => { setAiDerivedFields(prev => { const n = new Set(prev); n.delete("maxSectorsPerCrewMonth"); return n; }); set("maxSectorsPerCrewMonth")(v); }}
                testId="input-max-sectors-crew-month" min={40} max={150} />
              <NumInput label="Max Duties / Crew / Mo" value={p.maxDutiesPerCrewMonth || 20} aiDerived={isDerived("maxDutiesPerCrewMonth")}
                onChange={v => { setAiDerivedFields(prev => { const n = new Set(prev); n.delete("maxDutiesPerCrewMonth"); return n; }); set("maxDutiesPerCrewMonth")(v); }}
                testId="input-max-duties-crew-month" min={10} max={30} />
              <NumInput label="Sectors per Duty (FDP)" value={p.sectorsPerDuty || 2} aiDerived={isDerived("sectorsPerDuty")}
                onChange={v => { setAiDerivedFields(prev => { const n = new Set(prev); n.delete("sectorsPerDuty"); return n; }); set("sectorsPerDuty")(v); }}
                testId="input-sectors-per-duty" min={1} max={6} sub="Sectors per flight duty period" />
              <NumInput label="Min Pilots / Aircraft" value={p.minPilotsPerAircraft || 3.5} aiDerived={isDerived("minPilotsPerAircraft")}
                onChange={v => { setAiDerivedFields(prev => { const n = new Set(prev); n.delete("minPilotsPerAircraft"); return n; }); set("minPilotsPerAircraft")(v); }}
                testId="input-min-pilots-per-aircraft" step={0.5} min={2} max={8} sub="Regulatory crew ratio" />
              <NumInput label="Crew per Flight (CC)" value={p.crewPerFlight} aiDerived={isDerived("crewPerFlight")}
                onChange={v => { setAiDerivedFields(prev => { const n = new Set(prev); n.delete("crewPerFlight"); return n; }); set("crewPerFlight")(v); }}
                testId="input-crew-per-flight" min={1} max={20} sub="Cabin crew per sector" />
            </div>
            <NumInput label="Max CC Duties / Month" value={p.dutiesPerCrew} aiDerived={isDerived("dutiesPerCrew")}
              onChange={v => { setAiDerivedFields(prev => { const n = new Set(prev); n.delete("dutiesPerCrew"); return n; }); set("dutiesPerCrew")(v); }}
              testId="input-duties-per-crew" min={10} max={28} />
          </div>

          {/* Buffer Factors */}
          <div className="bg-card border border-border rounded-sm p-4 space-y-4">
            <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground/60 font-bold">
              Buffer Factors
              {aiDerivedFields.size > 0 && !aiLoading && (
                <span className="flex items-center gap-1 text-primary/60 normal-case font-normal text-xs"><Sparkles size={9} /> AI-calibrated</span>
              )}
            </div>
            <Slider label="Leave Buffer"    value={p.leavePct}    min={5}  max={20} step={1} unit="%" onChange={set("leavePct")}    testId="slider-leave"    aiDerived={isDerived("leavePct")} />
            <Slider label="Training Buffer" value={p.trainingPct} min={3}  max={15} step={1} unit="%" onChange={set("trainingPct")} testId="slider-training" aiDerived={isDerived("trainingPct")} />
            <Slider label="Sick Buffer"     value={p.sickPct}     min={2}  max={10} step={1} unit="%" onChange={set("sickPct")}     testId="slider-sick"     aiDerived={isDerived("sickPct")} />
            <Slider label="Reserve Buffer"  value={p.reservePct}  min={5}  max={25} step={1} unit="%" onChange={set("reservePct")}  testId="slider-reserve"  aiDerived={isDerived("reservePct")} />
            <div className="pt-1 border-t border-border/50 flex justify-between text-xs">
              <span className="text-muted-foreground">Total buffer applied</span>
              <span className={`font-mono font-bold ${totalBuf > 45 ? "text-red-400" : totalBuf > 35 ? "text-amber-400" : "text-green-400"}`}>{totalBuf}%</span>
            </div>
          </div>

          {/* Actual Headcount */}
          <div className="bg-card border border-border rounded-sm p-4 space-y-3">
            <div className="text-xs tracking-widest uppercase text-muted-foreground/60 font-bold">Actual Headcount &amp; Cost</div>
            <div className="grid grid-cols-2 gap-3">
              <NumInput label="Actual Pilots"     value={p.actualPilots}    onChange={set("actualPilots")}    testId="input-actual-pilots" min={0} />
              <NumInput label="Actual Cabin Crew" value={p.actualCabinCrew} onChange={set("actualCabinCrew")} testId="input-actual-crew"   min={0} />
              <NumInput label="Pilot Cost (₹L/mo)"  value={p.avgPilotCostLakhs}     step={0.1} onChange={set("avgPilotCostLakhs")}     testId="input-pilot-cost" min={0} />
              <NumInput label="Crew Cost (₹L/mo)"   value={p.avgCabinCrewCostLakhs} step={0.1} onChange={set("avgCabinCrewCostLakhs")} testId="input-crew-cost"  min={0} />
            </div>
          </div>

          {/* AI Rationale */}
          {aiRationale && !aiLoading && (
            <div className="bg-card border border-primary/20 rounded-sm p-4 space-y-2.5" data-testid="ai-rationale-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs tracking-widest uppercase text-primary/80 font-bold">
                  <Sparkles size={11} /> AI Parameter Rationale
                </div>
                <button onClick={() => callAiSuggest(aircraftType, p)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors" data-testid="recalibrate-btn">
                  <RefreshCw size={10} /> Recalibrate
                </button>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{aiRationale.rationale}</p>
              {aiRationale.cabin_crew_note && (
                <p className="text-xs text-purple-400/70 leading-relaxed border-t border-border/40 pt-2">{aiRationale.cabin_crew_note}</p>
              )}
            </div>
          )}

          {aiLoading && (
            <div className="bg-card border border-primary/10 rounded-sm p-4">
              <div className="flex items-center gap-2 text-xs text-primary animate-pulse">
                <Sparkles size={12} className="animate-spin" style={{ animationDuration: "2s" }} />
                AI analysing {selectedAircraft.label} operation profile...
              </div>
              <div className="mt-2 h-1 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-primary/40 rounded-full animate-pulse w-2/3" />
              </div>
            </div>
          )}

          {/* Run Button */}
          <button
            onClick={runCalculation}
            disabled={calcLoading || aiLoading}
            data-testid="run-calculation-btn"
            className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground text-sm font-bold rounded-sm transition-all hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {(calcLoading || aiLoading) ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
            {calcLoading ? "Calculating…" : aiLoading ? "AI calibrating parameters…" : "Run Multi-Method Calculation"}
          </button>
          {calcError && <div className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-sm px-3 py-2">{calcError}</div>}
        </div>

        {/* ── Right: Results ─────────────────────────────────────────────── */}
        <div className="col-span-3 space-y-4">

          {!r ? (
            <div className="h-64 flex items-center justify-center bg-card border border-border/50 rounded-sm" data-testid="results-placeholder">
              <div className="text-center space-y-2">
                <Calculator size={28} className="text-primary/30 mx-auto" />
                <p className="text-sm text-muted-foreground/60">Configure inputs and click <span className="text-primary font-bold">Run Multi-Method Calculation</span></p>
                <p className="text-xs text-muted-foreground/40">Results show MAX of: Flight-Hour · Sector-Based · Duty-Day · Regulatory</p>
              </div>
            </div>
          ) : (
            <>
              {/* KPI Summary */}
              <div className="grid grid-cols-4 gap-2" data-testid="kpi-summary">
                <KpiCard label="Pilots Required" value={r.required_pilots} color="text-primary" testId="kpi-pilots"
                  info="Final pilot headcount — the MAX of all four calculation methods plus reserve buffer. This is the minimum safe number of pilots needed to sustain operations without DGCA violations." />
                <KpiCard label="Pilot Gap"
                  value={r.actual_pilots > 0 ? gapLbl(r.pilot_gap, r.actual_pilots) : "—"}
                  color={r.actual_pilots > 0 ? gapCol(r.pilot_gap || 0) : "text-muted-foreground/30"}
                  sub={r.actual_pilots > 0 ? `Actual: ${r.actual_pilots}` : "Enter actual headcount"}
                  testId="kpi-pilot-gap"
                  info="Difference between your actual roster and the calculated requirement. Positive (+) = surplus — idle cost risk. Negative (−) = shortage — operational and safety risk requiring urgent recruitment." />
                <KpiCard label="Cabin Crew Req." value={r.required_cc} color="text-purple-400" testId="kpi-cc"
                  info="Final cabin crew headcount — the MAX of DGCA Regulatory (1:50), Industry Standard (1:38), and Demand-Based methods, after leave, training, sick, and reserve buffers are applied." />
                <KpiCard label="CC Gap"
                  value={r.actual_cabin_crew > 0 ? gapLbl(r.cc_gap, r.actual_cabin_crew) : "—"}
                  color={r.actual_cabin_crew > 0 ? gapCol(r.cc_gap || 0) : "text-muted-foreground/30"}
                  sub={r.actual_cabin_crew > 0 ? `Actual: ${r.actual_cabin_crew}` : "Enter actual headcount"}
                  testId="kpi-cc-gap"
                  info="Difference between actual cabin crew on roster and the calculated requirement. Positive = scheduling flexibility and reserve buffer. Negative = overtime risk, fatigue, and service quality degradation." />
              </div>

              {/* Fleet Demand Banner */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    label: "Total Fleet FH / Period", val: r.total_fh.toLocaleString(), icon: Plane, color: "text-blue-400",
                    info: "Fleet Size × Block FH/Day × Planning Days. Represents the total block hours that must be covered by all aircraft. This is the raw workload input for the Flight-Hour pilot calculation method.",
                  },
                  {
                    label: "Total Fleet Sectors", val: r.total_sectors.toLocaleString(), icon: Activity, color: "text-green-400",
                    info: "Fleet Size × Sectors/Day × Planning Days. Each sector requires one cockpit crew assignment. This is the raw demand input for the Sector-Based pilot calculation method.",
                  },
                  {
                    label: "Total Flight Duty Periods", val: Math.round(r.total_duties).toLocaleString(), icon: Users, color: "text-amber-400",
                    info: "Total Sectors ÷ Sectors per Duty Period. A Flight Duty Period (FDP) groups multiple sectors under one legal duty window. Fewer sectors per duty = more FDPs = higher crew demand. Input for the Duty-Day method.",
                  },
                ].map(item => (
                  <div key={item.label} className="bg-card border border-border rounded-sm p-3 flex items-center gap-3">
                    <item.icon size={18} className={`${item.color} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-lg font-mono font-bold ${item.color}`}>{item.val}</div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground leading-tight">
                        {item.label}
                        <InfoTooltip text={item.info} position="bottom" width="w-72" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Pilot Method Comparison ─────────────────────────────── */}
              <div className="bg-card border border-border rounded-sm p-4" data-testid="pilot-methods-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-primary font-bold">
                    Pilot Requirement — 4 Methods
                    <InfoTooltip text="Four independent methods compute the pilot requirement. The final figure is MAX of all four — whichever method produces the highest number is the 'binding constraint' and sets your minimum safe headcount." position="bottom" width="w-80" />
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-bold">
                    <Award size={11} className="text-primary" />
                    <span className="text-foreground">Binding: </span>
                    <span className="text-primary">{r.binding_pilot_method}</span>
                    <ChevronRight size={11} className="text-primary" />
                    <span className="text-2xl font-mono text-primary">{r.required_pilots}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <MethodCard
                    method="Flight-Hour Method"
                    value={r.pilot_methods.flight_hour.with_reserve}
                    isBinding={r.pilot_methods.flight_hour.is_binding}
                    icon={TrendingUp}
                    info="Formula: ⌈(Total FH ÷ Effective FH/Pilot)⌉ × 2 × (1 + Reserve%). Effective FH = DGCA monthly FH limit × (1 − leave% − training% − sick%). Multiplied by 2 for Captain + First Officer pair."
                    details={[
                      { label: "Total Fleet FH", val: `${r.total_fh.toLocaleString()} FH` },
                      { label: "Eff. FH / Pilot", val: `${r.pilot_methods.flight_hour.eff_pilot_fh} h` },
                      { label: "Base (pre-reserve)", val: r.pilot_methods.flight_hour.base },
                    ]}
                  />
                  <MethodCard
                    method="Sector-Based Method"
                    value={r.pilot_methods.sector_based.with_reserve}
                    isBinding={r.pilot_methods.sector_based.is_binding}
                    icon={Activity}
                    info="Formula: ⌈(Total Sectors ÷ Effective Sectors/Crew)⌉ × 2 × (1 + Reserve%). Effective Sectors = DGCA max sectors/month × (1 − buffers). Most relevant for high-frequency short-haul networks where sectors, not hours, are the constraint."
                    details={[
                      { label: "Total Sectors", val: r.total_sectors.toLocaleString() },
                      { label: "Eff. Sectors / Crew", val: r.pilot_methods.sector_based.eff_sectors_per_crew },
                      { label: "Base (pre-reserve)", val: r.pilot_methods.sector_based.base },
                    ]}
                  />
                  <MethodCard
                    method="Duty-Day Method"
                    value={r.pilot_methods.duty_day.with_reserve}
                    isBinding={r.pilot_methods.duty_day.is_binding}
                    icon={BarChart3}
                    info="Formula: ⌈(Total FDPs ÷ Effective Duties/Crew)⌉ × 2 × (1 + Reserve%). Effective Duties = Max duty days/month × (1 − buffers). Captures rest requirements between duty periods — binding when sectors per FDP are low."
                    details={[
                      { label: "Total FDPs", val: `${Math.round(r.total_duties).toLocaleString()}` },
                      { label: "Eff. Duties / Crew", val: r.pilot_methods.duty_day.eff_duties_per_crew },
                      { label: "Base (pre-reserve)", val: r.pilot_methods.duty_day.base },
                    ]}
                  />
                  <MethodCard
                    method="Regulatory Minimum"
                    value={r.pilot_methods.regulatory_min.required}
                    isBinding={r.pilot_methods.regulatory_min.is_binding}
                    icon={Award}
                    accentColor="text-amber-400"
                    info="Formula: Fleet Size × Min Pilots per Aircraft. DGCA mandates a minimum crew complement per aircraft in the fleet regardless of actual utilisation. No reserve buffer is applied — this is an absolute regulatory floor."
                    details={[
                      { label: "Fleet Size", val: `${r.pilot_methods.regulatory_min.min_per_aircraft}× per acft` },
                      { label: "Minimum Required", val: r.pilot_methods.regulatory_min.required },
                      { label: "Reserve Applied", val: "None (DGCA floor)" },
                    ]}
                  />
                </div>
                {/* Formula trace for binding method */}
                {(() => {
                  const bm = r.pilot_methods[
                    r.binding_pilot_method === "Flight-Hour" ? "flight_hour" :
                    r.binding_pilot_method === "Sector-Based" ? "sector_based" :
                    r.binding_pilot_method === "Duty-Day" ? "duty_day" : "regulatory_min"
                  ];
                  return bm?.formula ? (
                    <div className="mt-3 bg-primary/5 border border-primary/15 rounded-sm px-3 py-2">
                      <div className="text-xs text-muted-foreground/60 font-mono leading-relaxed">
                        <span className="text-primary/80 font-bold">Binding formula: </span>{bm.formula}
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>

              {/* ── Cabin Crew Method Comparison ───────────────────────── */}
              <div className="bg-card border border-border rounded-sm p-4" data-testid="cc-methods-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-purple-400 font-bold">
                    Cabin Crew — 3 Methods
                    <InfoTooltip text="Three methods compute cabin crew demand independently. The final figure is MAX of all three. DGCA Regulatory sets the legal floor; Industry Standard reflects competitive norms; Demand-Based reflects your actual scheduled rotations." position="bottom" width="w-80" />
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-bold">
                    <Award size={11} className="text-purple-400" />
                    <span className="text-foreground">Binding: </span>
                    <span className="text-purple-400">{r.binding_cc_method.split(" (")[0]}</span>
                    <ChevronRight size={11} className="text-purple-400" />
                    <span className="text-2xl font-mono text-purple-400">{r.required_cc}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    {
                      key: "dgca_regulatory", accentColor: "text-amber-400", icon: Award,
                      info: "DGCA CAR Section 8 Part I mandates a minimum of 1 cabin crew per 50 passenger seats (rounded up). This is the legal minimum — your CC headcount cannot fall below this figure under any scheduling scenario.",
                    },
                    {
                      key: "industry_standard", accentColor: "text-blue-400", icon: BarChart3,
                      info: "Industry best practice of 1 cabin crew per 38 seats (rounded up). Provides better service quality and fatigue safety margins. Most competitive full-service and LCC carriers operate at this ratio or higher.",
                    },
                    {
                      key: "demand_based", accentColor: "text-purple-400", icon: Users,
                      info: "Computed from your input CC-per-flight × total sectors ÷ effective monthly duties per crew member. Represents actual operational demand based on your planned rotation schedule, adjusted for all buffers.",
                    },
                  ].map(({ key, accentColor, icon, info }) => {
                    const m = r.cc_methods[key];
                    return (
                      <MethodCard key={key}
                        method={m.label}
                        value={m.with_reserve}
                        isBinding={m.is_binding}
                        accentColor={accentColor}
                        icon={icon}
                        info={info}
                        details={[
                          { label: "CC per Flight", val: m.cc_per_flight },
                          { label: "Base required", val: m.base },
                        ]}
                      />
                    );
                  })}
                </div>
              </div>

              {/* ── Gap Analysis Table ─────────────────────────────────── */}
              <div className="bg-card border border-border rounded-sm p-4" data-testid="gap-analysis-table">
                <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground font-bold mb-3">
                  Gap Analysis
                  <InfoTooltip text="Compares your actual headcount (entered above) against the calculated requirement. Enter actual pilot and cabin crew counts to activate gap analysis. Positive gap = surplus (cost risk). Negative gap = shortage (safety and operational risk)." position="bottom" width="w-80" />
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground">
                      <th className="text-left py-2 font-bold">Category</th>
                      <th className="text-right py-2 font-bold">
                        <span className="flex items-center justify-end gap-1">
                          Required
                          <InfoTooltip text="Calculated minimum headcount — MAX of all applicable methods with all buffers applied." position="top-right" width="w-60" />
                        </span>
                      </th>
                      <th className="text-right py-2 font-bold">
                        <span className="flex items-center justify-end gap-1">
                          Actual
                          <InfoTooltip text="Your current rostered headcount as entered in the Actual Headcount section." position="top-right" width="w-60" />
                        </span>
                      </th>
                      <th className="text-right py-2 font-bold">
                        <span className="flex items-center justify-end gap-1">
                          Gap
                          <InfoTooltip text="Actual − Required. Positive = surplus (over-staffed). Negative = shortage (under-staffed, risk of DGCA violation or service failure)." position="top-right" width="w-64" />
                        </span>
                      </th>
                      <th className="text-right py-2 font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { cat: "Pilots (Capt. + FO)", req: r.required_pilots, act: r.actual_pilots, gap: r.pilot_gap },
                      { cat: "Cabin Crew", req: r.required_cc, act: r.actual_cabin_crew, gap: r.cc_gap },
                      { cat: "Total Crew", req: r.required_pilots + r.required_cc, act: (r.actual_pilots || 0) + (r.actual_cabin_crew || 0), gap: (r.pilot_gap ?? 0) + (r.cc_gap ?? 0) },
                    ].map(({ cat, req, act, gap }) => (
                      <tr key={cat} className="border-b border-border/40 last:border-0">
                        <td className="py-2 font-medium text-foreground">{cat}</td>
                        <td className="py-2 text-right font-mono text-primary">{req.toLocaleString()}</td>
                        <td className="py-2 text-right font-mono text-foreground">{act > 0 ? act.toLocaleString() : "—"}</td>
                        <td className={`py-2 text-right font-mono font-bold ${gapCol(gap || 0)}`}>
                          {act > 0 ? (gap > 0 ? `+${gap}` : gap) : "—"}
                        </td>
                        <td className="py-2 text-right">
                          {act > 0 && <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold border ${gapBadge(gap, act)}`}>{gap > 0 ? "Surplus" : gap < 0 ? "Shortage" : "Balanced"}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Cost + Efficiency ──────────────────────────────────── */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="cost-impact-panel">
                  <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground font-bold">
                    Cost Impact
                    <InfoTooltip text="Financial exposure from staffing imbalances. Excess pilots and CC shortages both carry direct costs. Zero values indicate a well-balanced roster for that category." position="top-right" width="w-72" />
                  </div>
                  {[
                    {
                      lbl: "Excess Pilot Burn / mo",
                      val: r.cost.excess_pilot_burn_lakhs > 0  ? fmtCr(r.cost.excess_pilot_burn_lakhs / 100) : "—",
                      col: r.cost.excess_pilot_burn_lakhs > 0 ? "text-amber-400" : "text-green-400",
                      info: "Monthly payroll cost of surplus pilots above the calculated requirement. Computed as: (Actual − Required) × Avg Pilot Cost/Month. Represents preventable expenditure that could fund type-rating training or fleet expansion.",
                    },
                    {
                      lbl: "CC Shortage Risk / mo",
                      val: r.cost.cc_shortage_risk_lakhs > 0   ? fmtCr(r.cost.cc_shortage_risk_lakhs / 100) : "—",
                      col: r.cost.cc_shortage_risk_lakhs > 0  ? "text-red-400"   : "text-green-400",
                      info: "Estimated monthly cost of cabin crew shortfall, calculated at 1.5× base rate to account for overtime, agency hire premiums, and schedule compensation. Does not include fatigue-related DGCA penalty risk.",
                    },
                    {
                      lbl: "Total Impact / mo",
                      val: fmtCr(r.cost.total_monthly_crores),
                      col: "text-foreground",
                      info: "Combined monthly financial exposure from both excess pilot burn and cabin crew shortage risk. Use this as the baseline for workforce planning ROI conversations.",
                    },
                    {
                      lbl: "Annualised Impact",
                      val: fmtCr(r.cost.total_annual_crores),
                      col: "text-primary",
                      info: "Total monthly impact × 12 months. Useful for board-level workforce planning budgets, investor presentations, and long-term crew cost modelling.",
                    },
                  ].map(({ lbl, val, col, info }) => (
                    <div key={lbl} className="flex justify-between items-center border-b border-border/40 pb-2.5 last:border-0 last:pb-0">
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        {lbl}
                        <InfoTooltip text={info} position="top-left" width="w-72" />
                      </span>
                      <span className={`text-sm font-mono font-bold ${col}`}>{val}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="efficiency-metrics-panel">
                  <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground font-bold">
                    Efficiency Metrics
                    <InfoTooltip text="Operational efficiency indicators derived from your inputs and calculated requirements. Green = healthy, Amber = monitor, Red = action required." position="top-right" width="w-72" />
                  </div>
                  {[
                    {
                      label: "Pilot Utilisation", val: r.efficiency.pilot_utilisation_pct, warn: 75, bad: 60, invert: false,
                      info: "Effective pilot FH ÷ DGCA max FH × 100%. Measures what percentage of a pilot's available monthly hours are productively used after leave, training, and sick buffers. Target: >75%. Below 65% indicates over-staffing or scheduling inefficiency.",
                    },
                    {
                      label: "Pilot Idle Rate", val: r.efficiency.pilot_idle_pct, warn: 15, bad: 30, invert: true,
                      info: "Surplus pilots as a percentage of your actual headcount. A high idle rate signals avoidable payroll expenditure. Target: <15%. Above 30% = significant over-staffing; consider route expansion, secondments, or type-rating investment.",
                    },
                    {
                      label: "CC Stress Index", val: r.efficiency.cc_stress_index_pct, warn: 15, bad: 35, invert: true,
                      info: "Cabin crew shortfall as a percentage of the calculated requirement. Values above 20% indicate systemic overloading — direct drivers of crew fatigue, DGCA compliance risk, and inflight service degradation. Immediate recruitment is advised above 30%.",
                    },
                  ].map(({ label, val, warn, bad, invert, info }) => {
                    const isGood = invert ? val <= warn : val >= warn;
                    const isBad  = invert ? val >= bad  : val <= bad;
                    const color  = isBad ? "text-red-400" : isGood ? "text-green-400" : "text-amber-400";
                    const barC   = isBad ? "#f87171" : isGood ? "#4ade80" : "#fbbf24";
                    return (
                      <div key={label} className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            {label}
                            <InfoTooltip text={info} position="top-left" width="w-72" />
                          </span>
                          <span className={`font-mono font-bold ${color}`}>{val}%</span>
                        </div>
                        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(val, 100)}%`, backgroundColor: barC }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Optimisation Insights ──────────────────────────────── */}
              {r.insights.length > 0 && (
                <div className="bg-card border border-border rounded-sm p-4 space-y-2" data-testid="optimisation-insights">
                  <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">
                    Optimisation Insights
                    <span className="ml-2 text-primary font-mono normal-case">{r.insights.length} identified</span>
                  </div>
                  {r.insights.map((ins, i) => (
                    <InsightBadge key={i} type={ins.type} text={ins.text} />
                  ))}
                </div>
              )}

              {/* ── Save Run ───────────────────────────────────────────── */}
              <div className="bg-card border border-border rounded-sm p-4" data-testid="save-run-panel">
                <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-3">Save This Run</div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Label for this scenario…"
                    value={saveLabel}
                    onChange={e => setSaveLabel(e.target.value)}
                    data-testid="save-label-input"
                    className="flex-1 bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    onClick={handleSaveRun}
                    disabled={saveLoading || !saveLabel.trim()}
                    data-testid="save-run-btn"
                    className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm transition-all hover:bg-primary/90 disabled:opacity-50"
                  >
                    {saveLoading ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
                    {saveLoading ? "Saving…" : "Save Run"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
